// BudgetLines — the read-only budget-line register for the selected purchase
// order. Presentational: FinanceTab passes the selected PurchaseOrderWithDerived
// in, and this component renders each line against its derived balance
// (po.lineBalances). Each line's rateItemIds/milestoneIds/sowIds tag arrays are
// resolved to labels via the rateItemOptions/milestoneOptions/sowOptions props
// (id -> label lookups, falling back to the raw id when an option list doesn't
// carry it) and rendered as chips, alongside a per-line GST indicator
// (gstSuffix off line.gstApplies). A search box filters by line number /
// description / resolved tag labels. Consumption ("Invoiced") is
// adjustment-inclusive — po.lineBalances[line.id].consumed already sums
// approved-invoice splits PLUS the line's own signed adjustments ledger
// (opening balances, SAP true-ups); an InfoHint on the consumption columns
// states this so Remaining is not misread as covering the review queue. Each
// row can expand (caret, single-row-at-a-time like InvoiceQueue) to a consumed
// detail breaking "Invoiced" back out into its approved-invoiced subtotal —
// itemised as the approved invoices drawing on the line, each a button that
// hands the invoice id to `onOpenInvoice` (FinanceTab jumps to the invoices
// sub-view and expands the row) — plus the itemised adjustment entries, and a
// separate awaiting-review list for review/queried invoices touching the line
// (not yet deducted, mirroring the approved-only hint). The reference client
// PO carries ~350 lines,
// so rows are hard-capped (BUDGET_LINE_CAP) with a show-all footer rather than
// rendering every row up front. "Manage PO & lines" hands control back to
// FinanceTab to open the edit modal.

import { Fragment, useMemo, useState } from "react";
import { clsx } from "clsx";
import { AlertTriangle, ChevronRight, Search } from "lucide-react";
import InfoHint from "../../common/InfoHint";
import Button from "../../common/Button";
import {
  formatAud,
  validateLineBalance,
  type LineBalance,
} from "../../../utils/financeCalc";
import { gstSuffix } from "../../../utils/gstSuffix";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  Invoice,
  PoLine,
  PurchaseOrderWithDerived,
} from "../../../types/finance";
import type { TagOption } from "./TagMultiSelect";

// Hard render cap. The reference client PO has ~350 lines; rendering all of
// them on every PO switch is wasteful and janky, so the register shows the
// first N after filtering and offers an explicit show-all.
export const BUDGET_LINE_CAP = 50;

// Table column count — the caret column plus Line / Site-description / Tags /
// PO amount / Invoiced / Remaining / Balance. Kept as one constant so the
// empty-state row and the expanded detail row's colSpan can't drift apart.
const COLUMN_COUNT = 8;

const APPROVED_ONLY_HINT =
  "Approved invoices + adjustments only — amounts awaiting review are not yet deducted";

interface BudgetLinesProps {
  po: PurchaseOrderWithDerived;
  /** The contract's invoices (contract-wide; rows here filter to this PO) —
   *  feeds the expanded detail's per-line invoice lists. */
  invoices: Invoice[];
  /** Open the purchase-order edit modal (owned by FinanceTab). */
  onManagePo: () => void;
  /** Jump to an invoice — FinanceTab switches to the invoices sub-view and
   *  expands the row. */
  onOpenInvoice: (invoiceId: string) => void;
  /** id -> label options for resolving a line's rateItemIds tag chips. */
  rateItemOptions: TagOption[];
  /** id -> label options for resolving a line's milestoneIds tag chips. */
  milestoneOptions: TagOption[];
  /** id -> label options for resolving a line's sowIds tag chips. */
  sowOptions: TagOption[];
}

function optionsToLabelMap(options: TagOption[]): Map<string, string> {
  return new Map(options.map((o) => [o.id, o.label]));
}

// Resolves a line's tag ids to their labels, falling back to the raw id when
// an id isn't in the option list — defensive, same convention as
// TagMultiSelect's own chip rendering.
function resolveLabels(ids: string[], labelById: Map<string, string>): string[] {
  return ids.map((id) => labelById.get(id) ?? id);
}

interface TagLabelMaps {
  rateItem: Map<string, string>;
  milestone: Map<string, string>;
  sow: Map<string, string>;
}

function matchesSearch(line: PoLine, needle: string, maps: TagLabelMaps): boolean {
  const haystack = [
    line.lineNumber,
    line.description,
    ...resolveLabels(line.rateItemIds, maps.rateItem),
    ...resolveLabels(line.milestoneIds, maps.milestone),
    ...resolveLabels(line.sowIds, maps.sow),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

// Proportion of the line consumed, clamped to [0, 100] and guarded against a
// zero-total line so an unfunded line renders an empty track rather than NaN.
function consumedPct(balance: LineBalance): number {
  if (balance.lineTotal <= 0) return 0;
  return Math.min(100, Math.max(0, (balance.consumed / balance.lineTotal) * 100));
}

function barTone(balance: LineBalance): string {
  if (balance.overdrawn) return "bg-red-500 dark:bg-red-400";
  if (balance.nearLimit) return "bg-amber-500 dark:bg-amber-400";
  return "bg-emerald-500 dark:bg-emerald-400";
}

function remainingTone(balance: LineBalance): string {
  if (balance.overdrawn) return "text-red-600 dark:text-red-400";
  if (balance.nearLimit) return "text-amber-700 dark:text-amber-400";
  return "text-slate-900 dark:text-slate-100";
}

// Near-limit's colour cue fails WCAG 1.4.1 (colour is not the only channel
// conveying meaning) unless paired with a non-colour signal — the overdrawn
// row already carries one via its "-" sign, so near-limit needs an
// equivalent. Reuses the shared near-limit wording (validateLineBalance)
// against the line's current balance (no additional amount drawn) so the
// caption text matches the invoice-time validation copy exactly.
function nearLimitCaption(balance: LineBalance): string {
  return validateLineBalance(balance, 0).text;
}

// A ledger entry's sign is its primary signal (opening balance vs. true-up),
// so a positive adjustment renders with an explicit "+" — formatAud alone
// only distinguishes negative values (a leading "-").
function formatSignedAdjustment(amount: number): string {
  return amount >= 0 ? `+${formatAud(amount)}` : formatAud(amount);
}

interface LineInvoiceRow {
  invoice: Invoice;
  /** This invoice's draw on the line — its split amounts for the line summed
   *  across charges (an invoice can hit one line via several splits). */
  amount: number;
}

// The invoices drawing on one line of this PO, split by whether the money has
// actually been deducted: approved mirrors computeLineBalances' consumption;
// pending (review + queried) mirrors the approved-only hint's carve-out.
function invoicesForLine(
  invoices: Invoice[],
  poId: string,
  lineId: string,
): { approved: LineInvoiceRow[]; pending: LineInvoiceRow[] } {
  const approved: LineInvoiceRow[] = [];
  const pending: LineInvoiceRow[] = [];
  for (const invoice of invoices) {
    if (invoice.poId !== poId) continue;
    if (invoice.status === "draft") continue;
    let amount = 0;
    for (const charge of invoice.charges) {
      for (const split of charge.splits) {
        if (split.poLineId === lineId) amount += split.amount;
      }
    }
    if (amount <= 0) continue;
    (invoice.status === "approved" ? approved : pending).push({
      invoice,
      amount,
    });
  }
  return { approved, pending };
}

function LineInvoiceButton({
  row,
  onOpenInvoice,
}: {
  row: LineInvoiceRow;
  onOpenInvoice: (invoiceId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenInvoice(row.invoice.id)}
      className="flex w-full items-center justify-between gap-2 rounded-sm text-left hover:text-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:text-brand-400"
    >
      <span className="text-slate-600 underline decoration-slate-300 underline-offset-2 dark:text-slate-400 dark:decoration-slate-600">
        {row.invoice.invoiceNumber} ·{" "}
        {formatDayMonthYear(row.invoice.invoiceDate)}
        {/* A queried invoice is parked, not in the review queue — its pending
            row must say so or the list reads as all awaiting review. */}
        {row.invoice.status === "queried" && " · queried"}
      </span>
      <span className="tabular-nums font-medium text-slate-700 dark:text-slate-300">
        {formatAud(row.amount)}
      </span>
    </button>
  );
}

interface TagChipListProps {
  ids: string[];
  labelById: Map<string, string>;
  className: string;
}

function TagChipList({ ids, labelById, className }: TagChipListProps) {
  return (
    <>
      {ids.map((id) => (
        <span
          key={id}
          className={clsx(
            "inline-flex items-center rounded-full px-2 py-0.5 text-caption font-medium",
            className,
          )}
        >
          {labelById.get(id) ?? id}
        </span>
      ))}
    </>
  );
}

export default function BudgetLines({
  po,
  invoices,
  onManagePo,
  onOpenInvoice,
  rateItemOptions,
  milestoneOptions,
  sowOptions,
}: BudgetLinesProps) {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);

  const rateItemLabelById = useMemo(
    () => optionsToLabelMap(rateItemOptions),
    [rateItemOptions],
  );
  const milestoneLabelById = useMemo(
    () => optionsToLabelMap(milestoneOptions),
    [milestoneOptions],
  );
  const sowLabelById = useMemo(() => optionsToLabelMap(sowOptions), [sowOptions]);
  const tagMaps: TagLabelMaps = useMemo(
    () => ({ rateItem: rateItemLabelById, milestone: milestoneLabelById, sow: sowLabelById }),
    [rateItemLabelById, milestoneLabelById, sowLabelById],
  );

  const needle = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      needle === ""
        ? po.lines
        : po.lines.filter((line) => matchesSearch(line, needle, tagMaps)),
    [po.lines, needle, tagMaps],
  );

  const capped = showAll ? filtered : filtered.slice(0, BUDGET_LINE_CAP);
  const hiddenCount = filtered.length - capped.length;

  return (
    <div className="space-y-3" data-testid="finance-budget-lines-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label="Search budget lines"
            placeholder="Search line, description, rate item, milestone or SOW"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-sm w-64 pl-8"
          />
        </div>
        <Button variant="secondary" size="sm" onClick={onManagePo}>
          Manage PO &amp; lines
        </Button>
      </div>

      <p className="text-caption text-slate-500 dark:text-slate-400">
        {po.lines.length} {po.lines.length === 1 ? "line" : "lines"} · invoice
        charges validate against these balances
      </p>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800">
              <th scope="col" className="px-2 py-3 text-left">
                <span className="sr-only">Expand</span>
              </th>
              <th scope="col" className="px-4 py-3 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Line
              </th>
              <th scope="col" className="px-4 py-3 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Site / description
              </th>
              <th scope="col" className="px-4 py-3 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Tags
              </th>
              <th scope="col" className="px-4 py-3 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                PO amount
              </th>
              <th scope="col" className="px-4 py-3 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                <span className="inline-flex items-center gap-1">
                  Invoiced
                  <InfoHint text={APPROVED_ONLY_HINT} label="About invoiced amounts" />
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                <span className="inline-flex items-center gap-1">
                  Remaining
                  <InfoHint text={APPROVED_ONLY_HINT} label="About remaining budget" />
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMN_COUNT}
                  className="px-4 py-8 text-center text-body text-slate-500 dark:text-slate-400"
                >
                  No lines match your search.
                </td>
              </tr>
            ) : (
              capped.map((line) => {
                const bal = po.lineBalances[line.id];
                const expanded = expandedLineId === line.id;
                const adjustmentsTotal = line.adjustments.reduce(
                  (acc, a) => acc + a.amount,
                  0,
                );
                const approvedInvoiced = bal.consumed - adjustmentsTotal;
                const hasTags =
                  line.rateItemIds.length > 0 ||
                  line.milestoneIds.length > 0 ||
                  line.sowIds.length > 0;
                return (
                  <Fragment key={line.id}>
                    <tr
                      data-testid={`budget-line-row-${line.id}`}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-2 py-3">
                        <button
                          type="button"
                          aria-label={`${expanded ? "Collapse" : "Expand"} consumed detail for line ${line.lineNumber}`}
                          aria-expanded={expanded}
                          onClick={() =>
                            setExpandedLineId(expanded ? null : line.id)
                          }
                          className={clsx(
                            "inline-flex rounded-sm transition-transform focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                            expanded
                              ? "rotate-90 text-brand-600 dark:text-brand-400"
                              : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
                          )}
                        >
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-body tabular-nums text-slate-700 dark:text-slate-300">
                        {line.lineNumber}
                      </td>
                      <td className="px-4 py-3 text-body">
                        <span className="text-slate-900 dark:text-slate-100">
                          {line.description}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-body">
                        <div className="flex flex-wrap gap-1">
                          <TagChipList
                            ids={line.rateItemIds}
                            labelById={rateItemLabelById}
                            className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                          />
                          <TagChipList
                            ids={line.milestoneIds}
                            labelById={milestoneLabelById}
                            className="bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                          />
                          <TagChipList
                            ids={line.sowIds}
                            labelById={sowLabelById}
                            className="bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300"
                          />
                          {!hasTags && (
                            <span className="text-caption text-slate-400 dark:text-slate-500">
                              —
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-body tabular-nums text-slate-900 dark:text-slate-100">
                        {formatAud(bal.lineTotal)}{" "}
                        <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
                          ({gstSuffix(line.gstApplies)})
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-body tabular-nums text-slate-600 dark:text-slate-400">
                        {formatAud(bal.consumed)}
                      </td>
                      <td
                        data-testid={`budget-remaining-${line.id}`}
                        className={clsx(
                          "px-4 py-3 text-right text-body tabular-nums font-medium",
                          remainingTone(bal),
                        )}
                        title={bal.nearLimit ? nearLimitCaption(bal) : undefined}
                      >
                        <span className="inline-flex items-center justify-end gap-1">
                          {bal.nearLimit && (
                            <AlertTriangle
                              className="h-3.5 w-3.5 shrink-0"
                              aria-hidden="true"
                            />
                          )}
                          {formatAud(bal.remaining)}
                        </span>
                        {bal.nearLimit && (
                          <span className="sr-only"> — {nearLimitCaption(bal)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
                          role="img"
                          aria-label={`${formatAud(bal.consumed)} of ${formatAud(
                            bal.lineTotal,
                          )} invoiced`}
                        >
                          <div
                            className={clsx("h-full", barTone(bal))}
                            style={{ width: `${consumedPct(bal)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                    {expanded && (() => {
                      const lineInvoices = invoicesForLine(
                        invoices,
                        po.id,
                        line.id,
                      );
                      return (
                      <tr data-testid={`consumed-detail-${line.id}`}>
                        <td
                          colSpan={COLUMN_COUNT}
                          className="border-l-2 border-brand-500 bg-slate-50 p-3 dark:bg-slate-800/40"
                        >
                          <div className="space-y-2 text-caption">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-slate-600 dark:text-slate-400">
                                Approved invoiced
                              </span>
                              <span
                                data-testid={`approved-invoiced-${line.id}`}
                                className="tabular-nums font-medium text-slate-900 dark:text-slate-100"
                              >
                                {formatAud(approvedInvoiced)}
                              </span>
                            </div>
                            {lineInvoices.approved.length > 0 && (
                              <ul
                                data-testid={`line-invoices-approved-${line.id}`}
                                className="space-y-1 pl-3"
                              >
                                {lineInvoices.approved.map((row) => (
                                  <li key={row.invoice.id}>
                                    <LineInvoiceButton
                                      row={row}
                                      onOpenInvoice={onOpenInvoice}
                                    />
                                  </li>
                                ))}
                              </ul>
                            )}
                            {line.adjustments.length > 0 ? (
                              <ul className="space-y-1">
                                {line.adjustments.map((adjustment) => (
                                  <li
                                    key={adjustment.id}
                                    data-testid={`line-adjustment-${line.id}-${adjustment.id}`}
                                    className="flex items-center justify-between gap-2"
                                  >
                                    <span className="text-slate-600 dark:text-slate-400">
                                      {adjustment.label || "Adjustment"}
                                    </span>
                                    <span className="tabular-nums font-medium text-slate-700 dark:text-slate-300">
                                      {formatSignedAdjustment(adjustment.amount)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-slate-400 dark:text-slate-500">
                                No adjustments
                              </p>
                            )}
                            <div className="flex items-center justify-between gap-2 border-t border-slate-200 pt-1 dark:border-slate-700">
                              <span className="font-medium text-slate-700 dark:text-slate-300">
                                Total consumed
                              </span>
                              <span className="tabular-nums font-medium text-slate-900 dark:text-slate-100">
                                {formatAud(bal.consumed)}
                              </span>
                            </div>
                            {lineInvoices.pending.length > 0 && (
                              <div className="border-t border-slate-200 pt-1 dark:border-slate-700">
                                <p className="text-slate-500 dark:text-slate-400">
                                  Pending — not yet deducted
                                </p>
                                <ul
                                  data-testid={`line-invoices-pending-${line.id}`}
                                  className="mt-1 space-y-1 pl-3"
                                >
                                  {lineInvoices.pending.map((row) => (
                                    <li key={row.invoice.id}>
                                      <LineInvoiceButton
                                        row={row}
                                        onOpenInvoice={onOpenInvoice}
                                      />
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })()}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {hiddenCount > 0 && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-center dark:border-slate-700 dark:bg-slate-800/60">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-caption font-medium text-brand-600 hover:text-brand-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm dark:text-brand-400"
            >
              +{hiddenCount} more {hiddenCount === 1 ? "line" : "lines"} · show
              all
            </button>
          </div>
        )}
      </div>

      {!showAll && filtered.length > BUDGET_LINE_CAP && (
        <p className="text-caption text-slate-500 dark:text-slate-500">
          Showing the first {BUDGET_LINE_CAP} of {filtered.length} lines.
        </p>
      )}
    </div>
  );
}
